'use client';

/**
 * @coralogix/browser (RUM + OpenTelemetry, ~230 KB) is loaded on demand when an
 * app calls initCoralogix. The public surface stays synchronous: init marks the
 * SDK ready and every call made while it downloads is queued and flushed in
 * order, starting with the init itself, so callers' "init, then log" contract
 * is unchanged.
 *
 * Log calls made before initCoralogix used to reach an uninitialized SDK,
 * which dropped them with a console.debug; they now sit in the bounded queue
 * instead — equally invisible, but recovered if init happens later.
 */

type CoralogixRumSdk = typeof import('@coralogix/browser').CoralogixRum;

interface ICoralogixInitConfig {
  publicKey: string;
  application: string;
  version: string;
  domain?: string;
  sessionSampleRate?: number;
}

interface ICoralogixUserContext {
  userId: string;
  userName?: string;
  email?: string;
  metadata?: Record<string, string>;
}

// Pre-init calls were dropped by the SDK anyway; the cap only bounds a
// pathological error loop in a session that never initializes.
const MAX_PENDING_CALLS = 50;

let initialized = false;
let sdk: CoralogixRumSdk | undefined;
let pendingCalls: Array<(rum: CoralogixRumSdk) => void> = [];

const withCoralogix = (call: (rum: CoralogixRumSdk) => void): void => {
  if (sdk) {
    call(sdk);
    return;
  }
  if (pendingCalls.length === MAX_PENDING_CALLS) {
    pendingCalls.shift();
  }
  pendingCalls.push(call);
};

export const initCoralogix = (config: ICoralogixInitConfig): void => {
  if (typeof window === 'undefined') return;
  initialized = true;
  void import('@coralogix/browser')
    .then(({ CoralogixRum }) => {
      CoralogixRum.init({
        stringifyCustomLogData: true,
        public_key: config.publicKey,
        application: config.application,
        version: config.version,
        coralogixDomain:
          (config.domain as 'US1' | 'US2' | 'EU1' | 'EU2' | 'AP1' | 'AP2' | 'AP3' | 'STAGING') ??
          'US2',
        sessionConfig: {
          sessionSampleRate: config.sessionSampleRate ?? 30,
          alwaysTrackSessionsWithErrors: true,
        },
      });
      sdk = CoralogixRum;
      pendingCalls.splice(0).forEach((call) => call(CoralogixRum));
    })
    .catch(() => {
      // Telemetry failing to download must never surface to the player.
      pendingCalls = [];
    });
};

export const setCoralogixUserContext = (context: ICoralogixUserContext): void => {
  if (!initialized) return;
  withCoralogix((rum) =>
    rum.setUserContext({
      user_id: context.userId,
      user_name: context.userName ?? '',
      user_email: context.email ?? '',
      user_metadata: context.metadata ?? {},
    })
  );
};

export const setCoralogixLabels = (labels: Record<string, string>): void => {
  if (!initialized) return;
  withCoralogix((rum) => rum.setLabels(labels));
};

export const logCoralogixError = (name: string, data: Record<string, unknown>): void => {
  withCoralogix((rum) => rum.error(name, data));
};

export const logCoralogixInfo = (name: string, data: Record<string, unknown>): void => {
  withCoralogix((rum) => rum.info(name, data));
};

export const isCoralogixReady = (): boolean => initialized;
